// @flow
import * as React from 'react';
import BoardMergeTargetPickerFlyout from 'app/common/react/components/BoardEdit/BoardMergeTargetPickerFlyout';
import BoardMergeWarning from 'app/common/react/components/BoardEdit/BoardMergeWarning';
import i18n from 'app/common/lib/i18n';
import MyBoardsTypeaheadProvider from 'app/common/react/components/MyBoardsTypeaheadProvider/MyBoardsTypeaheadProvider';
import templateConst from 'app/common/lib/templateConst';
import type { BoardPrivacy } from 'app/packages/pinterest-flow-types/board';
import { Experiment } from 'app/common/react/components/experiments';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { Box, Button, Divider, Heading, Spinner, Text } from 'gestalt';

const FIELD_SET_KEY = 'merge';

export type MergeFlowBoard = {
  collaborated_by_me: boolean,
  collaborator_count: number,
  follower_count: number,
  id: string,
  is_collaborative: boolean,
  name: string,
  privacy: BoardPrivacy,
};

type OwnProps = {|
  sourceBoardId: string,
  onCancel: () => void,
  onSubmit: (selectedTargetBoard: MergeFlowBoard) => void,
|};

type PaginationProps = {|
  pageSize?: number,
|};

type HocProps = {|
  boardsResource: Resource<Array<MergeFlowBoard>>,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  viewer?: *,
|};

type Props = {|
  ...OwnProps,
  ...PaginationProps,
  ...HocProps,
|};

type State = {|
  selectedTargetBoard: ?MergeFlowBoard,
|};

class BoardMergeModalContent extends React.Component<Props, State> {
  state = {
    selectedTargetBoard: null,
  };

  handleChange = (selectedTargetBoard: ?MergeFlowBoard) => {
    this.setState({ selectedTargetBoard });
  };

  handleSubmit = () => {
    const { onSubmit } = this.props;
    const { selectedTargetBoard } = this.state;
    if (selectedTargetBoard) {
      onSubmit(selectedTargetBoard);
    }
  };

  renderModalBody = () => {
    const { boardsResource, sourceBoardId } = this.props;
    const { selectedTargetBoard } = this.state;
    const { data: boards } = boardsResource;

    if (!boards) {
      return (
        <Spinner
          accessibilityLabel={i18n._('Loading board list', 'spinner accessibility label')}
          show
        />
      );
    }

    const sourceBoard = boards.find((board) => board.id === sourceBoardId);
    const targetBoardCandidates = boards.filter((board) => board.id !== sourceBoardId);

    if (targetBoardCandidates.length === 0) {
      return (
        <Text>
          {i18n._(
            'There is no board to merge this board into.',
            'helper text for selecting merge target board',
          )}
        </Text>
      );
    }

    return (
      <Box>
        <Box paddingY={1}>
          <MyBoardsTypeaheadProvider fieldSetKey={FIELD_SET_KEY} allBoardsResource={boardsResource}>
            {(data) =>
              data.isLoaded ? (
                <BoardMergeTargetPickerFlyout
                  boards={data.results.filter((board) => board.id !== sourceBoardId)}
                  fetchMore={data.fetchMore}
                  onSearchQueryUpdate={data.onQueryChange}
                  onSelect={this.handleChange}
                  searchQuery={data.query}
                  selectedBoard={selectedTargetBoard}
                />
              ) : null
            }
          </MyBoardsTypeaheadProvider>
        </Box>
        {selectedTargetBoard &&
          sourceBoard && (
            <Box paddingY={2}>
              <BoardMergeWarning sourceBoard={sourceBoard} targetBoard={selectedTargetBoard} />
            </Box>
          )}
      </Box>
    );
  };

  render() {
    const {
      boardsResource: { data: boards },
      onCancel,
    } = this.props;
    const { selectedTargetBoard } = this.state;
    return (
      <Box>
        <Box display="flex" justifyContent="center" paddingY={5} paddingX={3}>
          <Heading size="xs">
            {!selectedTargetBoard
              ? i18n._('Move all Pins to...', 'title for board merging modal')
              : i18n._('Move Pins and delete board?', 'title for board merging modal')}
          </Heading>
        </Box>
        <Divider />
        <Box padding={3}>{this.renderModalBody()}</Box>
        <Divider />
        <Box padding={3} display="flex" justifyContent="end">
          <Box display="flex" marginLeft={-1} marginRight={-1}>
            <Box paddingX={1}>
              <Button inline onClick={onCancel} text={i18n._('Cancel')} />
            </Box>
            {!!boards && (
              <Box paddingX={1}>
                <Button
                  color="red"
                  disabled={!selectedTargetBoard}
                  inline
                  onClick={this.handleSubmit}
                  text={i18n._('Move Pins and Delete Board', 'button for merging a board')}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
}

const LegacyBoardMergeModalContent = withResource(
  {
    name: 'BoardsResource',
    key: 'boardsResource',
    options: {
      privacy_filter: 'all',
      sort: 'alphabetical',
      field_set_key: FIELD_SET_KEY,
      username: 'me',
    },
  },
  { allowStale: false },
)(BoardMergeModalContent);

const ExperimentalBoardMergeModalContent = withResource(
  {
    name: 'BoardsFeedResource',
    key: 'boardsResource',
    options: ({ pageSize }) => ({
      field_set_key: FIELD_SET_KEY,
      username: 'me',
      page_size: pageSize,
    }),
  },
  { allowStale: false },
)(BoardMergeModalContent);

export default function BoardMergeModalContentContainer(props: OwnProps) {
  return (
    <Experiment name="web_paginated_boards">
      {({ anyEnabled, group }) =>
        anyEnabled ? (
          <ExperimentalBoardMergeModalContent
            {...props}
            pageSize={group === 'enabled_50' ? 50 : templateConst.settings.API_MAX_PAGE_SIZE}
          />
        ) : (
          <LegacyBoardMergeModalContent {...props} />
        )
      }
    </Experiment>
  );
}
